import {t, Trans} from '@lingui/macro'
import type {ReactNode} from 'react'
import styled from 'styled-components'

import {Inline, Loading, Stack, Text, tokens} from '@pleo-io/telescope'

import {BillingInterval, Interval, PlanTypeName} from '@product-web/api/types/billing'
import type {PlanType} from '@product-web/api/types/plan'
import {dayjs} from '@product-web/dates/dayjs'
import {getCountry, useUser} from '@product-web/shell/user'

import {usePricingBreakDown} from '../pricing-helpers'

interface PlanBreakdownProps {
    planType: PlanType
    planInterval: Interval
    billingInterval: BillingInterval
}

const CenteredLoading = styled(Loading)`
    justify-self: center;
`

const Divider = styled.div`
    background-color: ${tokens.shade300};
    width: 100%;
    height: 1px;
`

const MaxWidthLabel = styled.span`
    max-width: 220px;
    text-align: left;
    margin-right: ${tokens.spacing20};
`

const StrikeThroughText = styled(Text).attrs({color: 'colorContentStaticQuiet'})`
    text-decoration: line-through;
`

const getPlanPeriodText = (planInterval: Interval) => {
    switch (planInterval) {
        case Interval.MONTH:
            return t`1 month`
        case Interval.YEAR:
            return t`12 months commitment`
        default:
            return undefined
    }
}

const PlanBreakdownRow = ({children}: {children: ReactNode}) => {
    return (
        <Stack space={24} justifyItems="stretch">
            <Inline justifyContent="space-between">{children}</Inline>
        </Stack>
    )
}

const RenewalText = ({planInterval}: {planInterval: Interval}) => {
    switch (planInterval) {
        case Interval.MONTH:
            return <Trans>Every month</Trans>
        case Interval.YEAR:
            return <span>{dayjs().add(1, 'year').format('Do MMMM YYYY')}</span>
        default:
            return null
    }
}

const Price = ({amount, discounted}: {amount: string; discounted?: string}) => {
    if (discounted) {
        return (
            <>
                <strong>{discounted}</strong>
                <StrikeThroughText>{amount}</StrikeThroughText>
            </>
        )
    }
    return <strong>{amount}</strong>
}

const YearlyCost = ({
    price,
    discountPrice,
    monthlyPrice,
    showMonthlyPrice,
}: {
    price: string
    discountPrice?: string
    monthlyPrice?: string
    showMonthlyPrice?: boolean
}) => {
    return (
        <Stack>
            <PlanBreakdownRow>
                <span>
                    <Trans>Total cost</Trans>
                </span>
                <Inline space={6}>
                    <Price amount={price} discounted={discountPrice} />
                </Inline>
            </PlanBreakdownRow>
            <Inline justifySelf="flex-end">
                {(showMonthlyPrice && monthlyPrice && (
                    <Trans>Billed monthly at {monthlyPrice}</Trans>
                )) || <Trans>Billed annually</Trans>}
            </Inline>
        </Stack>
    )
}

const PlanBreakdown = ({planType, planInterval, billingInterval}: PlanBreakdownProps) => {
    const planName = planType && PlanTypeName[planType]
    const billingPeriod = getPlanPeriodText(planInterval)
    const user = useUser()
    const country = getCountry(user)
    const {isValidating, monthlyPrice, yearlyPrice} = usePricingBreakDown(
        planType,
        planInterval,
        billingInterval,
        country,
    )
    return (
        <Stack space={12}>
            <Text variant="xlarge-accent" color="shade800">
                {planName}
                {billingPeriod ? `, ${billingPeriod}` : ''}
            </Text>
            {isValidating ? (
                <CenteredLoading />
            ) : (
                <Stack space={24}>
                    <PlanBreakdownRow>
                        <MaxWidthLabel>
                            <Trans>Additional users</Trans> <Trans>included</Trans>
                        </MaxWidthLabel>
                        <Inline space={6} flexShrink={0}>
                            <Price
                                amount={monthlyPrice.standardFormatted}
                                discounted={monthlyPrice.discountedFormatted}
                            />
                            <Trans>/month</Trans>
                        </Inline>
                    </PlanBreakdownRow>
                    <Divider />
                    {planInterval === Interval.YEAR && (
                        <>
                            <YearlyCost
                                price={yearlyPrice.standardFormatted}
                                discountPrice={yearlyPrice.discountedFormatted}
                                showMonthlyPrice={billingInterval === BillingInterval.MONTHLY}
                                monthlyPrice={
                                    monthlyPrice.discountedFormatted ||
                                    monthlyPrice.standardFormatted
                                }
                            />
                            <Divider />
                        </>
                    )}
                    <PlanBreakdownRow>
                        <Trans>Renews</Trans>
                        <Text weight="semibold">
                            <RenewalText planInterval={planInterval} />
                        </Text>
                    </PlanBreakdownRow>
                </Stack>
            )}
        </Stack>
    )
}

export default PlanBreakdown
